import { EventEmitter, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router } from "@angular/router";
import { BehaviorSubject, Observable, Subject, of } from "rxjs";
import { catchError, map, switchMap, tap, timeout } from "rxjs/operators";
import { Lookup } from "../models/lookup.model";
import { OrganizationLevel } from "../models/organizationLevel.model";
import { Enum } from '../models/enum.model';
import { LiveOption } from '../models/liveOption.model';
import { ParameterService } from "./parameter.service";
import { ParameterLookup, ParameterThreshold } from "../models/parameter.model";
import { OrganizationLevelPermissions } from "../models/organizationLevelPermissions";
import nameOf from "../shared/form/nameOf.helper";
import { LocalStorageConstants } from "../shared/constants/localStorage.constants";
import { findFirstCitedParameter } from "../shared/utils/cited-parameter.util";

@Injectable({
  providedIn: 'root'
})

export class CommonService {
  disableHeader = new BehaviorSubject<string>(this.router.url);
  headerPreviousRangeSubject = new BehaviorSubject<boolean>(false);
  liveOptionSubject = new BehaviorSubject<LiveOption>({ show: false, enabled: false });
  public nodesUpdated = new Subject<boolean>();
  public colors = {
    primary: '#25245C',
    // primary: '#5C3BA8',
    primaryLight: '#25246066',
    black: '#000',
    blackOverlay: '#00000038',
    green: '#6BB700',
    greyLight: '#b4b4b4',
    greyOverlay: '#0000000d',
    greyDark: '#6E6E73',
    lightPurple: '#ad9dd3',
    red: '#B71C1C',
    yellowLight: '#f7f00a4d',
    yellow: '#FAD204',
    white: '#fff',
    popupOverlay: '#00000033',
    shadow: '#a6b3c24d',
    dust: '#8e44ad',
    noise: '#9b59b6',
    gas: '#6c3483',
    voc: '#a569bd',
    limeGreen: '#33FF00',
    nodeCluster: '#5C3BA8',
    nodeClusterLight: '#A984FF',
    airNowCluster: '#70AD4780',
    airNowClusterLight: '#90EE9080'
  };

  public batteryImageNames = {
    green: 'green',
    yellow: 'yellow',
    red: 'red',
  };

  public sidebarPath = '';
  public parameterThresholds: ParameterThreshold[] = [];
  selectParameterEmitter = new BehaviorSubject(null);
  selectedOrgEmitter: BehaviorSubject<OrganizationLevel | null> = new BehaviorSubject<OrganizationLevel | null>(null);
  selectedDateFilterEmitter = new BehaviorSubject(null);
  isSidenavCollapsed = false;
  sidenavToggleEmitter = new EventEmitter();

  private readonly CITED_PARAMETER_TIMEOUT_MS = 5000;

  constructor(private router: Router, private parameterService: ParameterService) { }

  setSelectedParameter(parameter: Lookup) {
    this.selectParameterEmitter.next(parameter);
    this.setParameterThresholds(String(parameter.id));
    localStorage.setItem(LocalStorageConstants.selectedParameter, JSON.stringify(parameter));
  }

  selectCitedParameter(parameterNames: string | null | undefined): Observable<ParameterLookup | null> {
    if (!parameterNames?.trim()) {
      return of(null);
    }
    return this.parameterService.getAllExcludingNotificationOnly().pipe(
      map((lookups) => {
        const current: ParameterLookup | null = this.selectParameterEmitter.value;
        return { current, match: findFirstCitedParameter(parameterNames, lookups, current) };
      }),
      tap(({ current, match }) => {
        if (match && match.id !== current?.id) {
          this.setSelectedParameter(match);
        }
      }),
      map(({ match }) => match)
    );
  }

  selectedParameterAfterCitation(parameterNames: string | null | undefined, onLookupError: (error: unknown) => void): Observable<ParameterLookup | null> {
    return this.selectCitedParameter(parameterNames).pipe(
      timeout(this.CITED_PARAMETER_TIMEOUT_MS),
      catchError((error: unknown) => {
        onLookupError(error);
        return of(null);
      }),
      switchMap(() => this.selectParameterEmitter)
    );
  }

  setSelectedOrg(org: OrganizationLevel) {
    this.selectedOrgEmitter.next(org);
  }

  getSelectedOrganizationLevelId(): string | null {
    return this.selectedOrgEmitter.value?.id ?? null;
  }

  selectedDateFilter(dateFilter) {
    this.selectedDateFilterEmitter.next(dateFilter);
  }

  isIhRoute(): boolean {
    const ihPermission = nameOf<OrganizationLevelPermissions>(x => x.canAccessIhModule);
    let snapshot: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (snapshot) {
      if (snapshot.data.requiredPermission === ihPermission) {
        return true;
      }
      snapshot = snapshot.firstChild;
    }
    return false;
  }

  getEnumName(enumObject: any, type: number) {
    const raw = enumObject[type];

    if (raw == null) return '';

    if (typeof raw !== 'string') return String(raw);

    const parts = raw.match(/[A-Z][a-z]+|[0-9]+|[A-Z]+(?![a-z])/g);

    if (parts && parts.length) return parts.join(' ');

    return raw.replace(/_/g, ' ');
  }

  getEnumNameListArray(enumObject: any) {
    return Object.keys(enumObject).filter((item) => {
      return isNaN(Number(item));
    }).map((res) => {
      return new Enum(Number(enumObject[res]), this.getEnumName(enumObject, enumObject[res]));
    });
  }

  setParameterThresholds(id: string) {
    this.parameterService.getParameterById(id).subscribe({
      next: (res) => {
        this.parameterThresholds = res?.thresholds ?? [];
      },
      error: () => {
        this.parameterThresholds = [];
      },
    });
  }

  getColorCode(value) {
    return value.replace('#', '');
  }

  computeMassContribution(taskAssignmentDataSource) {
    if (taskAssignmentDataSource) {
      taskAssignmentDataSource.forEach((taskAssignment) => {
        if (taskAssignment) {
          taskAssignment.massContributionPercentage = (taskAssignment.massContribution * 100).toFixed(2) + '%';
          taskAssignment.massContributionPercentageNum = Math.round(taskAssignment.massContribution * 100);
          taskAssignment.timeOnShiftPercent = (taskAssignment.timeOnShift * 100).toFixed(2) + '%';
          taskAssignment.timeOnShiftPercentageNum = Math.round(taskAssignment.timeOnShift * 100);
          for (let i = 0; i < taskAssignment.locations?.length; i++) {
            taskAssignment.locations[i]['timeOnShiftPercent'] = (taskAssignment.locations[i].timeOnShift > 0 ?
              ((taskAssignment.locations[i].timeOnShift) * 100) : 0).toFixed(2) + ' %';
            taskAssignment.locations[i]['massContributionPercent'] = (taskAssignment.locations[i].massContribution > 0 ?
              ((taskAssignment.locations[i].massContribution) * 100) : 0).toFixed(2) + ' %';
          }
        }
      });
    }
    return taskAssignmentDataSource;
  }
}
